import React from 'react';
import { useTranslation } from 'react-i18next';
import { subDays } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { ArrowDownRight, ArrowUpRight, Info } from 'lucide-react';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { cn } from '@/lib/utils';
import {
  useStockValueSummary,
  useInventoryAging,
  useInventoryValueMovement,
} from '../hooks/useReportingV2';
import { Skeleton } from '@/components/ui/skeleton';
import { AsOfDatePicker, WaterfallRangeControl } from './stock-value/StockValueControls';
import { StockValueBreakdowns } from './stock-value/StockValueBreakdowns';
import { TopSkusByValueCard } from './stock-value/TopSkusByValueCard';
import { InventoryAgingCard } from './stock-value/InventoryAgingCard';
import { ValueMovementWaterfall } from './stock-value/ValueMovementWaterfall';
import { deltaBand, RAG_TEXT } from '../lib/reporting-format';

interface StockValueTabProps {
  organizationId: string | null;
  locationId: string | null;
  active: boolean;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/**
 * Stock Value tab. The snapshot views are governed by an "as of" date (default
 * today) — NOT a range. The value-movement waterfall has its OWN date-range
 * control, scoped to that card only. Location filter is inherited from the page.
 */
export const StockValueTab: React.FC<StockValueTabProps> = ({
  organizationId,
  locationId,
  active,
}) => {
  const { t } = useTranslation('inventory');
  const { format, currency } = useCurrencyFormatter();

  // Snapshot control: a single as-of date.
  const [asOf, setAsOf] = React.useState<Date>(() => new Date());

  // Waterfall control: an independent range.
  const [wfPreset, setWfPreset] = React.useState('30');
  const [wfRange, setWfRange] = React.useState<DateRange | undefined>();

  const { wfStart, wfEnd } = React.useMemo(() => {
    if (wfPreset === 'custom' && wfRange?.from && wfRange?.to) {
      const s = new Date(wfRange.from);
      s.setHours(0, 0, 0, 0);
      return { wfStart: s, wfEnd: endOfDay(wfRange.to) };
    }
    const days = parseInt(wfPreset, 10) || 30;
    const end = endOfDay(new Date());
    const start = subDays(new Date(), days);
    start.setHours(0, 0, 0, 0);
    return { wfStart: start, wfEnd: end };
  }, [wfPreset, wfRange]);

  // Snapshot queries — gated on active.
  const summary = useStockValueSummary({
    organizationId,
    asOfDate: asOf,
    locationId,
    enabled: active,
  });
  const aging = useInventoryAging({ organizationId, locationId, enabled: active });

  // Waterfall query — independent range. Only enabled once the custom range is
  // complete, so an in-progress custom pick doesn't fire a half-formed query.
  const wfReady = wfPreset !== 'custom' || Boolean(wfRange?.from && wfRange?.to);
  const movement = useInventoryValueMovement({
    organizationId,
    startDate: wfStart,
    endDate: wfEnd,
    locationId,
    enabled: active && wfReady,
  });

  const s = summary.data;
  const dBand = deltaBand(s?.value30dAgo != null && s ? s.totalValue - s.value30dAgo : 0);
  const delta = s ? s.totalValue - s.value30dAgo : 0;
  const DeltaArrow = dBand === 'bad' ? ArrowDownRight : ArrowUpRight;

  return (
    <div className="space-y-6">
      {/* Controls row: as-of governs the snapshot. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <AsOfDatePicker value={asOf} onChange={setAsOf} />
      </div>

      {/* Headline */}
      <section className="rounded-lg border bg-card p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('reporting.v2.totalStockValue', 'Total stock value')}
            </p>
            {summary.isLoading && !s ? (
              <Skeleton className="mt-2 h-10 w-48" />
            ) : (
              <div className="mt-1 flex flex-wrap items-end gap-x-3 gap-y-1">
                <span className="text-4xl font-bold tabular-nums leading-none">
                  {format(s?.totalValue ?? 0, currency)}
                </span>
                {s && delta !== 0 && (
                  <span
                    className={cn(
                      'inline-flex shrink-0 items-center gap-0.5 pb-1 text-sm font-semibold tabular-nums',
                      RAG_TEXT[dBand],
                    )}
                  >
                    <DeltaArrow className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {delta > 0 ? '+' : ''}
                    {format(delta, currency)}
                  </span>
                )}
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              {t('reporting.v2.headlineBasis', 'Valued at WAC, vs 30 days ago')}
            </p>
          </div>

          {s && s.missingCostSkus > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-amber-300/80">
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
              {t('reporting.v2.missingCost', '{{n}} SKUs missing cost', {
                n: s.missingCostSkus,
              })}
            </span>
          )}
        </div>
      </section>

      {/* Breakdowns: location / product type / category */}
      <StockValueBreakdowns
        loading={summary.isLoading}
        byLocation={s?.byLocation ?? []}
        byProductType={s?.byProductType ?? []}
        byCategory={s?.byCategory ?? []}
      />

      {/* Top SKUs + Aging */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <TopSkusByValueCard loading={summary.isLoading} rows={s?.topSkus ?? []} />
        <InventoryAgingCard loading={aging.isLoading} data={aging.data} />
      </div>

      {/* Value-movement waterfall — its own range control. */}
      <ValueMovementWaterfall
        loading={movement.isLoading}
        data={movement.data}
        control={
          <WaterfallRangeControl
            preset={wfPreset}
            onPresetChange={setWfPreset}
            range={wfRange}
            onRangeChange={setWfRange}
          />
        }
      />
    </div>
  );
};
